import { KeyboardEvent, useState } from 'react';

import { Button, FormItem, ModalPrimitive as Modal } from '@ingrid/luterkort-react';

import { SimpleSearchSelect } from '~src/components/SimpleSearchSelect/SimpleSearchSelect';
import { SimpleSelect } from '~src/components/SimpleSelect/SimpleSelect';
import { useTranslation } from '~src/i18n/use-translation';
import { useDetailedMerchantListQuery } from '~src/pages/merchant/queries';

interface FormValues {
  merchantId?: string;
  siteId?: string;
}

interface Props {
  onSubmit: (data: FormValues) => void;
  defaultValue?: FormValues;
  onClose: () => void;
  isOpen: boolean;
  cancelable?: boolean;
}

export function MerchantSiteDialog({ onClose, onSubmit, isOpen, defaultValue, cancelable = false }: Props) {
  const { merchants } = useDetailedMerchantListQuery();
  const { t } = useTranslation();

  const [merchantId, setMerchantId] = useState<string | undefined>(defaultValue?.merchantId);
  const [siteId, setSiteId] = useState<string | undefined>(defaultValue?.siteId);

  const sites = merchants.find(m => m.id === merchantId)?.sites ?? [];

  function handleCancel() {
    onClose();
  }

  /**
   * Since we're taking control over the modal context and not using Modal.Trigger
   * we also need to control the outside interactions.
   */
  function handleInteractOutside() {
    if (!cancelable) {
      return;
    }

    handleCancel();
  }

  /**
   * Since we're taking control over the modal context and not using Modal.Trigger
   * we also need to control the keyboard interactions, such as pressing escape.
   */
  function handleKeyUp(event: KeyboardEvent) {
    if (event.key === 'Escape' && cancelable) {
      handleCancel();
    }
  }

  function handleSubmit() {
    if (merchantId && siteId) {
      onSubmit({ merchantId, siteId });
    }
  }

  return (
    <Modal.Root open={isOpen}>
      <Modal.Overlay />

      <Modal.Content onInteractOutside={handleInteractOutside} onKeyUp={handleKeyUp} style={{ width: '480px' }}>
        <Modal.Header>
          <Modal.Title>{t('merchantSelect.selectMerchantAndSite')}</Modal.Title>
          {cancelable && <Modal.CloseIconButton onClick={handleCancel} />}
        </Modal.Header>

        <Modal.Body>
          <form id="form-merchant-select" onSubmit={handleSubmit} style={{ display: 'grid', gap: '1.5rem' }}>
            <FormItem.Root>
              <FormItem.Label>{t('merchantSelect.merchant')}</FormItem.Label>
              <FormItem.Control>
                <SimpleSearchSelect
                  data-testid="merchant-select"
                  options={merchants.map(merchant => [merchant.id!, merchant.name!])}
                  placeholder={t('merchantSelect.selectMerchant')}
                  value={merchantId ? [merchantId] : undefined}
                  onValueChange={([value]) => {
                    setSiteId(merchants.find(m => m.id === value)?.sites[0].id);
                    setMerchantId(value);
                  }}
                />
              </FormItem.Control>
            </FormItem.Root>

            {sites.length > 1 && (
              <FormItem.Root>
                <FormItem.Label>{t('merchantSelect.site')}</FormItem.Label>
                <FormItem.Control>
                  <SimpleSelect
                    data-testid="site-select"
                    options={sites.map(site => [site.id!, site.name!])}
                    onValueChange={([value]) => setSiteId(value)}
                    placeholder={t('merchantSelect.selectSite')}
                    value={siteId ? [siteId] : undefined}
                    aria-required={true}
                  />
                </FormItem.Control>
              </FormItem.Root>
            )}
          </form>
        </Modal.Body>

        <Modal.Footer>
          {cancelable && (
            <Button variant="secondary" onClick={handleCancel}>
              {t('common.cancel')}
            </Button>
          )}

          <Button form="form-merchant-select" type="submit">
            {t('common.submit')}
          </Button>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
}
